import { ItemsPerPage } from '@billy/design-system'
import { useEffect, useMemo, useRef } from 'react'
import { useQuery } from 'react-query'

import { fetchRecurringInvoices } from '@features/recurringInvoices/query-api'

import { defaultStaleTime } from '../../../../../config/queryClient'
import { QueryKeys } from '../../../../../enums/queryKeys'
import { Paging } from '../../../../../types/metable'
import { useRecurringInvoicesListFilters } from '../context/recurringInvoicesListFilters'

export const useFetchRecurringInvoices = () => {
  const previousPagination = useRef<Paging | undefined>(undefined)
  const [{ sortDirection, sortProperty, page, pageSize, status }] = useRecurringInvoicesListFilters()

  const queryParams = useMemo(() => {
    return {
      ...(page ? { page } : {}),
      ...(pageSize ? { pageSize: pageSize as ItemsPerPage } : {}),
      ...(sortDirection ? { sortDirection } : {}),
      ...(sortProperty ? { sortProperty } : {}),
      ...(status ? { status } : {}),
    }
  }, [page, pageSize, sortDirection, sortProperty, status])

  const queryKey = useMemo(() => [QueryKeys.RecurringInvoices, queryParams], [queryParams])

  const { data, isFetched, isFetching, isLoading, isSuccess } = useQuery(
    queryKey,
    () => fetchRecurringInvoices(queryParams),
    {
      keepPreviousData: true,
      staleTime: defaultStaleTime,
    },
  )

  // If pagination exists then copy the value to reference and use it when original pagination during loading returns `undefined` value.
  useEffect(() => {
    if (data?.data?.meta.paging) {
      previousPagination.current = { ...data?.data?.meta.paging }
    }
  }, [data?.data?.meta.paging])

  const recurringInvoices = useMemo(() => data?.data?.recurringProfiles || [], [data?.data?.recurringProfiles])

  return {
    recurringInvoices,
    pagination: data?.data.meta.paging || previousPagination.current,
    isFetched,
    isFetching,
    isLoading,
    isSuccess,
  }
}
